<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 my-4">
      <div class="col my-3">
        <h1>Participating Providers
          <b-button class="ml-3" variant="outline-orange" @click="invitePayee()">
            <font-awesome-icon class="orange" icon="plus"/>
            Invite
          </b-button>
        </h1>
      </div>
    </div>

    <div class="container-fluid">
      <div class="row data-table-header">
        <div class="col-2">Provider/Facility Name</div>
        <div class="col-3">Name</div>
        <div class="col-2">NPI</div>
        <div class="col-3">E-Mail</div>
        <div class="col-2"></div>
      </div>
      <div v-for="payee in payees" class="row data-table-row" :key="payee.organizationPayeeId">
        <div class="column col-2">{{ payee.businessName }}</div>
        <div class="column col-3">{{ displayName(payee) }}</div>
        <div class="column col-2"><div v-if="payee.npi > 0" >{{ payee.npi}}</div></div>
        <div class="column col-3">{{ displayEmail(payee) }}</div>
        <div class="col-2">
          <div class="text-right"><button @click.prevent="editPayee(payee)" class="btn btn-outline-orange m-1">
            <font-awesome-icon class="orange mr-2" :icon="['far', 'edit']"/>
            Edit
          </button>
          </div>
        </div>
      </div>
    </div>

    <div class="alert alert-secondary" role="alert" v-show="!payees.length && !generalErrorMessage">
      &nbsp;&nbsp;No payees have been added.
    </div>

    <div class="row justify-content-center m-2">
      <div class="alert alert-danger col-6" role="alert" v-show="generalErrorMessage">
        {{ generalErrorMessage }}
      </div>
    </div>

    <loading-modal :visible="showingLoadingModal" :return-focus="$refs.searchInput"
                   @update:visible="visible => showingLoadingModal = visible"></loading-modal>
    <paginator :totalPages="totalPages" :currentPage="currentPage"
               @update:currentPage="newPage => currentPage = newPage"></paginator>
    <payee-modal :visible="showingPayeeModal" :edit-payee="editingPayee" @update:visible="visible => showingPayeeModal = visible" @payeeSaved="payeeSaved()"></payee-modal>
    <payee-invite-modal :visible="showingPayeeInviteModal" @update:visible="visible => showingPayeeInviteModal = visible" @payeeInvited="payeeInvited()"></payee-invite-modal>
  </div>
</template>

<script>
  import LoadingModal from '@/components/LoadingModal'
  import Paginator from '@/components/Paginator'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import moment from 'moment-timezone'
  import api from '@/services/api'
  import PayeeModal from '@/components/PayeeModal'
  import PayeeInviteModal from '@/components/PayeeInviteModal'

  export default {
    name: 'Payees',

    components: { LoadingModal, Paginator, PayeeModal, PayeeInviteModal },

    mixins: [ErrorsMixin],

    data () {
      return {
        payees: [],
        showingPayeeModal: false,
        showingPayeeInviteModal: false,
        showingLoadingModal: false,
        totalPages: 0,
        currentPage: 1,
        pageSize: 20,
        isLoading: false,
        editingPayee: {
          organizationPayeeId: 0
        }
      }
    },

    watch: {
      currentPage (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.load()
        }
      }
    },

    computed: {
    },

    methods: {
      displayName (payee) {
        if (payee.users && payee.users.length > 0) {
          let name = ''
          const payeeUser = payee.users[0]
          if (payeeUser.user && payeeUser.user.firstName) {
            name += payeeUser.user.firstName
          }
          if (payeeUser.user && payeeUser.user.lastName) {
            name += ' ' + payeeUser.user.lastName
          }

          return name
        }
      },

      displayEmail (payee) {
        if (payee.users && payee.users.length > 0) {
          const payeeUser = payee.users[0]

          if (payeeUser.user && payeeUser.user.emailAddress) {
            return payeeUser.user.emailAddress
          }
          if (payeeUser.inviteEmail) {
            return payeeUser.inviteEmail
          }

          return ''
        }
      },

      editPayee (payee) {
        this.editingPayee = payee
        this.showingPayeeModal = true
      },

      payeeSaved () {
        this.showingPayeeModal = false
        this.load()
      },

      invitePayee () {
        this.showingPayeeInviteModal = true
      },

      payeeInvited () {
        this.showingPayeeInviteModal = false
        this.load()
      },

      load (silent = false) {
        if (!silent) {
          this.showingLoadingModal = true
        }

        if (this.isLoading) {
          return
        }
        const parameters = {
          pageSize: this.pageSize,
          page: this.currentPage
        }

        this.isLoading = true
        api.post('/Dashboard/Payees', parameters).then(response => {
          this.isLoading = false
          this.payees = response.data.payees
          this.payees.forEach(payee => {
            payee.users.sort((lhs, rhs) => {
              return (lhs.organizationPayeeId > rhs.organizationPayeeId) ? 1 : ((rhs.organizationPayeeId > lhs.organizationPayeeId) ? -1 : 0)
            })
          })
          this.currentPage = response.data.page
          this.totalPages = Math.ceil(response.data.totalPayees / this.pageSize)
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.$store.logErrorResponse(this.$options.name, 'LoadError', error)
            this.setFromApiErrorResponse(error)
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },

      formatDate (date) {
        if (date) {
          return moment(date).format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatNumber (number) {
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return '0.00'
      },

      handleLogin () {
        this.load()
      },

      handleOrgChange (newOrg, event) {
        this.load()
      }
    },

    created () {
      this.$store.$on('organization:changed', this.handleOrgChange)
      this.$store.$on('user:authenticated', this.handleLogin)

      if (this.$store.isAuthenticated) {
        this.load()
      }
    }
  }
</script>

<style scoped>

</style>
